import React from "react";

const LAST_UPDATED = "1 April 2026";
const COMPANY_NAME = "M&J Evans Construction Ltd.";
const APP_NAME = "Waypoint Site Operations";
const CONTACT_EMAIL = "sales@mjevansconstruction.co.uk";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white text-gray-800 font-sans">
      <div className="max-w-3xl mx-auto px-6 py-12">

        {/* Header */}
        <div className="mb-10 border-b border-gray-200 pb-6">
          <p className="text-sm font-semibold uppercase tracking-widest text-indigo-600 mb-2">
            {COMPANY_NAME}
          </p>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Privacy Policy</h1>
          <p className="text-sm text-gray-500">Last updated: {LAST_UPDATED}</p>
        </div>

        {/* Intro */}
        <section className="mb-8">
          <p className="text-base leading-relaxed text-gray-700">
            This Privacy Policy describes how <strong>{COMPANY_NAME}</strong> ("we", "us", or "our")
            collects, uses, and protects information when you use the{" "}
            <strong>{APP_NAME}</strong> mobile application ("App"). By using the App, you agree
            to the practices described in this policy.
          </p>
        </section>

        {/* 1 */}
        <Section title="1. Information We Collect">
          <SubSection heading="a) Account &amp; Authentication Data">
            <p>
              When you sign in, we collect your name, email address, and authentication
              tokens provided through your organisation's identity provider (OAuth 2.0 /
              OpenID Connect). We do not store your password.
            </p>
          </SubSection>

          <SubSection heading="b) Photos &amp; Camera">
            <p>
              With your permission, the App can access your device's photo library or
              camera to let you attach images to tasks, site reports, and related records.
              Images are uploaded to our secure servers and associated with your account
              and the relevant project.
            </p>
          </SubSection>

          <SubSection heading="c) Usage &amp; Device Data">
            <p>
              We may collect non-personal technical data such as device type, operating
              system version, app version, and crash/error reports. This data is used
              solely to diagnose issues and improve the App.
            </p>
          </SubSection>

          <SubSection heading="d) Local Storage">
            <p>
              The App stores certain data locally on your device (via SQLite) to enable
              offline access. This includes cached project, task, and schedule information
              associated with your account.
            </p>
          </SubSection>
        </Section>

        {/* 2 */}
        <Section title="2. How We Use Your Information">
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>Authenticate and manage your account.</li>
            <li>Display and manage site schedules, tasks, and project information.</li>
            <li>Attach user-submitted photos to project records.</li>
            <li>Improve App performance, reliability, and security.</li>
            <li>Comply with applicable legal obligations.</li>
          </ul>
        </Section>

        {/* 3 */}
        <Section title="3. Data Sharing &amp; Disclosure">
          <p className="text-gray-700 mb-3">
            We do not sell or rent your personal data. We may share information in the
            following limited circumstances:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>
              <strong>Within your organisation:</strong> project managers and authorised
              colleagues within your company may see your name, presence status, and
              task activity.
            </li>
            <li>
              <strong>Service providers:</strong> trusted third-party vendors that help us
              operate the App (e.g., cloud hosting, error monitoring) under strict
              data-processing agreements.
            </li>
            <li>
              <strong>Legal requirements:</strong> where required by law, court order, or
              government authority.
            </li>
          </ul>
        </Section>

        {/* 4 */}
        <Section title="4. Data Retention">
          <p className="text-gray-700">
            We retain your personal data for as long as your account is active or as
            needed to provide services. If you request account deletion, we will delete
            or anonymise your personal data within 30 days, except where retention is
            required by law or for legitimate business purposes.
          </p>
        </Section>

        {/* 5 */}
        <Section title="5. Security">
          <p className="text-gray-700">
            We implement industry-standard technical and organisational measures to
            protect your data, including encrypted data transmission (TLS), secure
            OAuth-based authentication, and access controls. No method of transmission
            over the internet is 100% secure; we cannot guarantee absolute security.
          </p>
        </Section>

        {/* 6 */}
        <Section title="6. Your Rights">
          <p className="text-gray-700 mb-3">
            Depending on your location, you may have the following rights regarding your
            personal data:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>Access a copy of the personal data we hold about you.</li>
            <li>Correct inaccurate or incomplete data.</li>
            <li>Request deletion of your personal data.</li>
            <li>Withdraw consent where processing is based on consent.</li>
            <li>Lodge a complaint with a data protection supervisory authority.</li>
          </ul>
          <p className="text-gray-700 mt-3">
            To exercise any of these rights, contact us at{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-indigo-600 underline hover:text-indigo-800"
            >
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>

        {/* 7 */}
        <Section title="7. Children's Privacy">
          <p className="text-gray-700">
            The App is intended for use by adults in a professional workplace context.
            We do not knowingly collect personal data from anyone under 16 years of age.
          </p>
        </Section>

        {/* 8 */}
        <Section title="8. Third-Party Links &amp; Services">
          <p className="text-gray-700">
            The App may integrate with third-party services (e.g., your organisation's
            identity provider). This policy does not cover those third parties' privacy
            practices; please review their policies separately.
          </p>
        </Section>

        {/* 9 */}
        <Section title="9. Changes to This Policy">
          <p className="text-gray-700">
            We may update this Privacy Policy from time to time. When we do, we will
            revise the "Last updated" date at the top of this page. Continued use of
            the App after changes constitutes acceptance of the updated policy.
          </p>
        </Section>

        {/* 10 */}
        <Section title="10. Contact Us">
          <p className="text-gray-700">
            If you have any questions or concerns about this Privacy Policy, please
            contact us:
          </p>
          <address className="mt-3 not-italic text-gray-700 space-y-1">
            <p className="font-semibold">{COMPANY_NAME}</p>
            <p>
              Email:{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-indigo-600 underline hover:text-indigo-800"
              >
                {CONTACT_EMAIL}
              </a>
            </p>
          </address>
        </Section>

        {/* Footer */}
        <footer className="mt-12 pt-6 border-t border-gray-200 text-center text-xs text-gray-400">
          &copy; {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
        </footer>
      </div>
    </div>
  );
}

/* ─── helper components ─────────────────────────────────────────────────── */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-semibold text-gray-900 mb-3">{title}</h2>
      {children}
    </section>
  );
}

function SubSection({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <h3
        className="text-base font-medium text-gray-800 mb-1"
        dangerouslySetInnerHTML={{ __html: heading }}
      />
      <div className="text-gray-700 leading-relaxed">{children}</div>
    </div>
  );
}
